import React, { useEffect, useMemo, useState } from "react";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea,
} from "recharts";
import { EMOTIONS, EMOTION_LABELS_TR, EMOTION_EMOJI, formatDateTR, RESEARCH_START, RESEARCH_END } from "../lib/constants";
import { fetchTimeline } from "../lib/api";

const COUNTRY_COLORS = [
  "#EF4444", "#F97316", "#EAB308", "#22C55E", "#3B82F6",
  "#A855F7", "#06B6D4", "#EC4899", "#10B981", "#F43F5E", "#8B5CF6", "#FBBF24",
];

export default function Timeline({ countriesByCode }) {
  const [emotion, setEmotion] = useState("kaygi");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTimeline(emotion)
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [emotion]);

  const chartData = useMemo(() => {
    const byDate = new Map();
    for (const it of items) {
      const key = it.statement_date;
      if (!byDate.has(key)) byDate.set(key, { date: key, ts: new Date(key).getTime() });
      byDate.get(key)[it.country_code] = it.emotions?.[emotion] ?? null;
    }
    return Array.from(byDate.values()).sort((a, b) => a.ts - b.ts);
  }, [items, emotion]);

  const countryList = Object.values(countriesByCode);
  const startTs = new Date(RESEARCH_START).getTime();
  const endTs = new Date(RESEARCH_END + "T23:59:59Z").getTime();

  return (
    <div className="space-y-4" data-testid="timeline-view">
      <Card className="rounded-sm bg-[#141414] border-white/10 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <div className="data-label mb-1">Duygu Filtresi</div>
            <Select value={emotion} onValueChange={setEmotion}>
              <SelectTrigger data-testid="timeline-emotion-filter"
                             className="w-56 rounded-sm bg-[#0A0A0A] border-white/15 text-white font-mono">
                <SelectValue placeholder="Duygu seçin" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10 text-white">
                {EMOTIONS.map((e) => (
                  <SelectItem key={e} value={e} data-testid={`timeline-option-${e}`}>
                    {EMOTION_EMOJI[e]} {EMOTION_LABELS_TR[e]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto data-label">
            {loading ? "Yükleniyor..." : `${items.length} kayıt · ${RESEARCH_START} — ${RESEARCH_END} vurgulu`}
          </div>
        </div>

        <div className="mt-6 h-[420px]" data-testid="timeline-chart">
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="ts" type="number" domain={["dataMin", "dataMax"]}
                     tickFormatter={(t) => new Date(t).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}
                     stroke="#52525B"
                     tick={{ fill: "#9CA3AF", fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <YAxis domain={[0, 100]} stroke="#52525B"
                     tick={{ fill: "#9CA3AF", fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <ReferenceArea x1={startTs} x2={endTs} y1={0} y2={100} fill="#EF4444" fillOpacity={0.06} stroke="#EF4444" strokeOpacity={0.4} />
              <Tooltip contentStyle={{
                background: "#0A0A0A", border: "1px solid #27272A",
                fontFamily: "JetBrains Mono", fontSize: 12,
              }} labelFormatter={(t) => formatDateTR(new Date(t).toISOString())} />
              <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11 }} />
              {countryList.map((c, idx) => (
                <Line key={c.code} type="monotone" dataKey={c.code} name={c.name_tr}
                      stroke={COUNTRY_COLORS[idx % COUNTRY_COLORS.length]} strokeWidth={1.5}
                      dot={{ r: 2 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
